import { ItemSettingsService } from '@supply-mover-2-0/core';
import { useQuery } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';
import { NotFoundPage } from '../pages/NotFoundPage';
import { fetchPortalSite } from '../services/portalSiteService';
import { usePortalSiteStore } from '../stores/portalSiteStore';

interface TenantAuthOptions {
	phoneLoginEnabled: boolean;
	/**
	 * Which sign-in method the login modal leads with. Server-coerced:
	 * 'phone' is only ever served when phone login is actually enabled.
	 */
	defaultLoginMethod: 'email' | 'phone';
}

interface TenantContext {
	alias: string;
	name: string;
	authOptions: TenantAuthOptions;
}

const TenantCtx = createContext<TenantContext | null>(null);

/** Access the current validated tenant alias + name. */
export function useTenant(): TenantContext {
	const ctx = useContext(TenantCtx);
	if (ctx == null) {
		throw new Error('useTenant must be used inside TenantGate');
	}
	return ctx;
}

interface TenantGateProps {
	children: ReactNode;
}

/**
 * Validates the tenant + brand from the current subdomain before rendering
 * children, using a single bootstrap call to `GET /api/portal/site`.
 *
 * The bootstrap endpoint resolves everything the browser needs to render
 * its first frame: tenant alias/name, brand alias/displayName/logoUrl,
 * portalSiteId (threaded into subsequent login + API calls), and the
 * tenant-level auth options. It 404s when the tenant/brand isn't
 * configured; that's treated as the not-found state, not an error.
 *
 * The bootstrap response is the SINGLE source of truth for which tenant
 * we're rendering — including for custom domains (customer-owned hostnames
 * like `shop.customer.com`) where the hostname can't be parsed into a
 * tenant alias client-side. We intentionally do NOT gate on a
 * hostname-parsed alias here: the backend already resolves tenant + brand
 * from the request (subdomain, X-Portal-Tenant header, or custom-domain
 * Origin), and a bare/unknown domain comes back as `notConfigured` → 404
 * via the same path below. Gating on the parsed alias used to 404 every
 * custom domain before its (successful) bootstrap was ever consulted.
 *
 * Two outcomes:
 * 1. Tenant/brand not configured (or bare/unknown domain) → 404 page
 * 2. Valid → renders children with TenantContext available
 */
export function TenantGate({ children }: TenantGateProps): JSX.Element {
	const setSite = usePortalSiteStore((s) => s.setSite);
	const setNotConfigured = usePortalSiteStore((s) => s.setNotConfigured);

	const {
		data: bootstrap,
		isLoading: siteLoading,
		isError: siteError,
	} = useQuery({
		queryKey: ['portal-site-bootstrap'],
		queryFn: fetchPortalSite,
		retry: false,
		staleTime: Infinity,
	});

	useEffect(() => {
		if (bootstrap == null) {
			return;
		}

		if (bootstrap.notConfigured) {
			setNotConfigured();
			return;
		}

		setSite(bootstrap.site);

		// Seed the global ItemSettingsService with the tenant's
		// `maxPriceDecimalPrecision` from the bootstrap. This drives
		// `formatPrice()` in libs/core, which the order detail page
		// uses for unit price rendering — without this seed every
		// tenant would show the default 2 decimals regardless of what
		// they have configured.
		if (bootstrap.site?.maxPriceDecimalPrecision != null) {
			ItemSettingsService.setMaxPriceDecimalPrecision(
				bootstrap.site.maxPriceDecimalPrecision,
			);
		}

		// Reflect the brand in the browser tab title — small UX win that
		// makes a multi-brand tenant feel like distinct sites at a glance.
		if (bootstrap.site?.displayName != null) {
			document.title = bootstrap.site.displayName;
		}
	}, [bootstrap, setNotConfigured, setSite]);

	if (siteLoading) {
		// Don't paint our own bg here — index.html's <style> block
		// already paints html+body in `var(--brand-primary, #0F1A2A)`,
		// so an empty min-h-dvh shell lets the brand primary show
		// through during the bootstrap fetch. This kills the white /
		// gray-950 flash that used to show before the tenant resolved
		// (the "Workd Portal black screen" the QA pass flagged).
		return <div className="min-h-dvh" />;
	}

	if (
		siteError ||
		bootstrap == null ||
		bootstrap.notConfigured ||
		bootstrap.site == null
	) {
		return <NotFoundPage />;
	}

	// Valid tenant + resolved brand — provide context and render the app.
	// The brand's displayName takes precedence over the tenant's name so
	// multi-brand tenants show the right label; authOptions flow through
	// from the same bootstrap response so the login form knows which
	// methods to render without a second round trip.
	return (
		<TenantCtx.Provider
			value={{
				alias: bootstrap.site.tenantAlias,
				name: bootstrap.site.displayName,
				authOptions: {
					...bootstrap.site.authOptions,
					// `?? 'email'` tolerates a not-yet-deployed API that
					// doesn't return the key — the modal then leads with
					// email/password exactly as before.
					defaultLoginMethod:
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the DTO types the key as required, but an older deployed crm-api omits it at runtime
						bootstrap.site.defaultLoginMethod ?? 'email',
				},
			}}
		>
			{children}
		</TenantCtx.Provider>
	);
}
